"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Textarea,
  VStack,
  useColorModeValue,
  Text,
  Flex,
  useToast,
  Divider,
  Checkbox,
  IconButton,
  ModalOverlay,
  Input,
  ModalHeader,
  FormControl,
  FormLabel,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  RadioGroup,
  Radio,
  Tooltip,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import { Trash2, ChevronDown, Info } from "lucide-react";
import { VscCallOutgoing } from "react-icons/vsc";
import type { Node } from "./types";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface NodePropertiesDialogProps {
  node: Node;
  onClose: () => void;
  onUpdate: (updatedNode: Node, originalNodeId: string) => void;
  onDelete: (nodeId: string) => void;
  onSetAsStarting: (nodeId: string) => void;
  isStartingNode: boolean;
}

export const NodePropertiesDialog: React.FC<NodePropertiesDialogProps> = ({
  node,
  onClose,
  onUpdate,
  onDelete,
  onSetAsStarting,
  isStartingNode,
}) => {
  const [stateName, setStateName] = useState(
    node.data?.state_name || node.id || ""
  );
  const [statePrompt, setStatePrompt] = useState(node.data?.state_prompt || "");
  const [enableInterruptionSensitivity, setEnableInterruptionSensitivity] =
    useState(false);
  const [interruptionSensitivityValue, setInterruptionSensitivityValue] =
    useState(1); // Default to 1 (maximum sensitivity)
  const [isEditing, setIsEditing] = useState(false);
  const [stateNameError, setStateNameError] = useState("");
  const [toolNameErrors, setToolNameErrors] = useState<Record<number, string>>(
    {}
  );
  const [tools, setTools] = useState<any[]>([]);
  const [jsonSchemaError, setJsonSchemaError] = useState<
    Record<number, string>
  >({});
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Store the original node ID for reference
  const originalNodeId = node.id;

  // Update local state if node props change
  useEffect(() => {
    setStateName(node.data?.state_name || node.id || "");
    setStatePrompt(node.data?.state_prompt || "");
  }, [node]);

  useEffect(() => {
    setEnableInterruptionSensitivity(!!node.data?.interruption_sensitivity);

    // Set the sensitivity value if it exists and is a number between 0 and 1
    if (
      typeof node.data?.interruption_sensitivity === "number" &&
      node.data?.interruption_sensitivity >= 0 &&
      node.data?.interruption_sensitivity <= 1
    ) {
      setInterruptionSensitivityValue(node.data.interruption_sensitivity);
    } else if (!!node.data?.interruption_sensitivity) {
      // If it's enabled but not a valid number, default to 1
      setInterruptionSensitivityValue(1);
    }
  }, [node.data?.interruption_sensitivity]);

  // Initialize tools from node data
  useEffect(() => {
    if (node.data?.tools && Array.isArray(node.data.tools)) {
      // Deep clone the tools to avoid reference issues
      const initializedTools = JSON.parse(JSON.stringify(node.data.tools));

      // Convert any parameters objects to JSON strings for custom tools
      initializedTools.forEach((tool) => {
        if (
          tool.type === "custom" &&
          tool.parameters &&
          typeof tool.parameters === "object"
        ) {
          tool.parameters = JSON.stringify(tool.parameters, null, 2);
        }

        // Handle custom SIP headers for transfer_call tools
        if (tool.type === "transfer_call" && tool.custom_sip_headers) {
          const headers = Object.entries(tool.custom_sip_headers).map(
            ([key, value]) => ({
              key,
              value: value as string,
              error: validateSipHeaderKey(key),
            })
          );
          tool.custom_sip_headers = headers;
        }

        // Handle transfer_call tool options based on new schema
        if (tool.type === "transfer_call" && tool.transfer_option) {
          if (tool.transfer_option.type === "cold_transfer") {
            tool.transferType = "cold";
            tool.displayedPhoneNumber = tool.transfer_option
              .show_transferee_as_caller
              ? "transferee"
              : "retell";
          } else if (tool.transfer_option.type === "warm_transfer") {
            tool.transferType = "warm";
            if (tool.transfer_option.public_handoff_option) {
              tool.warmTransferOptionType =
                tool.transfer_option.public_handoff_option.type;
              if (
                tool.transfer_option.public_handoff_option.type ===
                "static_message"
              ) {
                tool.warmTransferMessage =
                  tool.transfer_option.public_handoff_option.message;
              } else if (
                tool.transfer_option.public_handoff_option.type === "prompt"
              ) {
                tool.warmTransferPrompt =
                  tool.transfer_option.public_handoff_option.prompt;
              }
            }
          }
        }
      });

      setTools(initializedTools);
    } else {
      setTools([]);
    }
  }, [node.data?.tools]);

  const validateStateName = (name: string) => {
    if (!name.trim()) {
      setStateNameError("State name cannot be empty");
      return false;
    }

    if (name.length > 64) {
      setStateNameError("State name must be 64 characters or less");
      return false;
    }

    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(name)) {
      setStateNameError(
        "State name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }

    setStateNameError("");
    return true;
  };

  const validateToolName = (name: string, index: number) => {
    // Check for spaces and invalid characters
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(name)) {
      setToolNameErrors((prev) => ({
        ...prev,
        [index]:
          "No spaces allowed. Allowed characters: a-z, A-Z, 0-9, _ and -.",
      }));
      return false;
    }

    // Clear error if valid
    setToolNameErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
    return true;
  };

  const validateJsonSchema = (schema: string, index: number) => {
    if (!schema.trim()) {
      // Empty schema is valid (no parameters)
      setJsonSchemaError((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      return true;
    }

    try {
      const parsedSchema = JSON.parse(schema);

      // Basic validation for JSON Schema format
      if (parsedSchema.type !== "object") {
        setJsonSchemaError((prev) => ({
          ...prev,
          [index]: "Schema root type must be 'object'",
        }));
        return false;
      }

      // Clear error if valid
      setJsonSchemaError((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      return true;
    } catch (error) {
      setJsonSchemaError((prev) => ({
        ...prev,
        [index]: "Invalid JSON format",
      }));
      return false;
    }
  };

  const validateSipHeaderKey = (key: string) => {
    if (!key.startsWith("X-") && !key.startsWith("x-")) {
      return `Custom SIP header keys must start with X- for key: ${key}`;
    }
    return "";
  };

  const handleAddSipHeader = (toolIndex: number) => {
    const updatedTools = [...tools];
    if (!updatedTools[toolIndex].custom_sip_headers) {
      updatedTools[toolIndex].custom_sip_headers = [];
    }
    updatedTools[toolIndex].custom_sip_headers.push({
      key: "",
      value: "",
      error: "",
    });
    setTools(updatedTools);
  };

  const handleUpdateSipHeader = (
    toolIndex: number,
    headerIndex: number,
    field: "key" | "value",
    value: string
  ) => {
    const updatedTools = [...tools];
    if (!updatedTools[toolIndex].custom_sip_headers) {
      updatedTools[toolIndex].custom_sip_headers = [];
    }

    updatedTools[toolIndex].custom_sip_headers[headerIndex][field] = value;

    if (field === "key") {
      updatedTools[toolIndex].custom_sip_headers[headerIndex].error =
        validateSipHeaderKey(value);
    }

    setTools(updatedTools);
  };

  const handleRemoveSipHeader = (toolIndex: number, headerIndex: number) => {
    const updatedTools = [...tools];
    if (updatedTools[toolIndex].custom_sip_headers) {
      updatedTools[toolIndex].custom_sip_headers.splice(headerIndex, 1);
      setTools(updatedTools);
    }
  };

  const handleStateNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setStateName(newName);
    validateStateName(newName);
  };

  const formatJsonSchema = (index: number) => {
    try {
      const tool = tools[index];
      if (tool.parameters && typeof tool.parameters === "string") {
        const formatted = JSON.stringify(JSON.parse(tool.parameters), null, 2);
        handleUpdateTool(index, "parameters", formatted);
      }
    } catch (error) {
      // If there's an error parsing, don't update
      console.error("Error formatting JSON:", error);
    }
  };

  // Example JSON schemas for custom tool
  const exampleSchemas = [
    {
      type: "object",
      properties: {
        name: {
          type: "string",
          description: "The name of the person",
        },
        age: {
          type: "integer",
          description: "The age of the person",
        },
      },
      required: ["name"],
    },
    {
      type: "object",
      properties: {
        query: {
          type: "string",
          description: "Search query",
        },
        limit: {
          type: "integer",
          description: "Maximum number of results to return",
        },
      },
      required: ["query"],
    },
    {
      type: "object",
      properties: {
        product_id: {
          type: "string",
          description: "The ID of the product",
        },
        quantity: {
          type: "integer",
          description: "The quantity to order",
        },
        options: {
          type: "object",
          properties: {
            color: {
              type: "string",
              description: "Product color",
            },
            size: {
              type: "string",
              description: "Product size",
            },
          },
        },
      },
      required: ["product_id", "quantity"],
    },
  ];

  // Add a function to handle adding a tool
  const handleAddTool = (toolType: string) => {
    let newTool: any = {
      type: toolType,
      name: `${toolType}_${tools.length + 1}`,
      description: "",
    };

    // Add tool-specific properties based on type
    if (toolType === "transfer_call") {
      newTool = {
        ...newTool,
        transfer_destination: {
          type: "predefined",
          number: "+12134447777",
        },
        transfer_option: {
          // Default to cold transfer
          type: "cold_transfer",
          show_transferee_as_caller: false,
        },
      };
    } else if (toolType === "press_digit") {
      newTool.delay_ms = 1000;
    } else if (
      toolType === "check_availability_cal" ||
      toolType === "book_appointment_cal"
    ) {
      newTool.cal_api_key = "";
      newTool.event_type_id = 0; // Using number as per API reference
      newTool.timezone = "America/Los_Angeles";
    } else if (toolType === "custom") {
      newTool = {
        ...newTool,
        url: "",
        description: "",
        speak_during_execution: true,
        speak_after_execution: true,
        execution_message_description:
          "The message you will say to user when calling this tool. Make sure it fits into the conversation smoothly.",
        timeout_ms: 120000, // Default 2 minutes
        parameters: "", // Empty string for JSON schema
      };
    }

    setTools([...tools, newTool]);
  };

  // Add a function to handle updating a tool
  const handleUpdateTool = (index: number, field: string, value: any) => {
    const updatedTools = [...tools];
    let current = updatedTools[index];

    // Special handling for transfer_option type change
    if (field === "transfer_option.type") {
      if (value === "cold_transfer") {
        current.transfer_option = {
          type: "cold_transfer",
          show_transferee_as_caller: false, // Default for cold
        };
      } else if (value === "warm_transfer") {
        current.transfer_option = {
          type: "warm_transfer",
          public_handoff_option: {
            type: "static_message", // Default for warm
            message: "",
          },
        };
      }
    } else if (field.includes(".")) {
      const parts = field.split(".");
      // Navigate to the nested object
      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (!current[part]) {
          current[part] = {};
        }
        current = current[part];
      }
      // Set the value on the deepest level
      current[parts[parts.length - 1]] = value;
    } else {
      current[field] = value;
    }

    // Validate tool name if the field is 'name'
    if (field === "name") {
      validateToolName(value, index);
    }

    // Validate JSON schema if the field is 'parameters' for custom tool
    if (field === "parameters" && updatedTools[index].type === "custom") {
      validateJsonSchema(value, index);
    }

    setTools(updatedTools);
  };

  // Add a function to handle deleting a tool
  const handleDeleteTool = (index: number) => {
    const updatedTools = [...tools];
    updatedTools.splice(index, 1);
    setTools(updatedTools);
  };

  const handleSave = () => {
    if (!validateStateName(stateName)) {
      toast({
        title: "Invalid state name",
        description: stateNameError,
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Validate all tool names
    let hasToolNameError = false;
    tools.forEach((tool, index) => {
      if (!validateToolName(tool.name, index)) {
        hasToolNameError = true;
      }
    });

    if (hasToolNameError) {
      toast({
        title: "Invalid tool name",
        description:
          "One or more tools have invalid names. Tool names must only contain letters, numbers, underscores, and dashes (no spaces).",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Validate JSON schemas for custom tools
    let hasJsonSchemaError = false;
    tools.forEach((tool, index) => {
      if (
        tool.type === "custom" &&
        tool.parameters &&
        !validateJsonSchema(tool.parameters, index)
      ) {
        hasJsonSchemaError = true;
      }
    });

    if (hasJsonSchemaError) {
      toast({
        title: "Invalid JSON schema",
        description:
          "One or more custom tools have invalid JSON schema parameters.",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Validate SIP headers for transfer_call tools
    let hasInvalidSipHeaders = false;
    tools.forEach((tool) => {
      if (tool.type === "transfer_call" && tool.custom_sip_headers) {
        const hasErrors = tool.custom_sip_headers.some(
          (header) => header.key && validateSipHeaderKey(header.key)
        );
        if (hasErrors) {
          hasInvalidSipHeaders = true;
        }
      }
    });

    if (hasInvalidSipHeaders) {
      toast({
        title: "Invalid SIP header keys",
        description: "All SIP header keys must start with X-",
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Process custom tool parameters from string to object
    const formattedTools = tools.map((tool) => {
      if (tool.type === "custom" && tool.parameters) {
        try {
          // If parameters is a non-empty string, parse it to an object
          if (typeof tool.parameters === "string" && tool.parameters.trim()) {
            return {
              ...tool,
              parameters: JSON.parse(tool.parameters),
            };
          }
          // If empty string, remove parameters property
          if (typeof tool.parameters === "string" && !tool.parameters.trim()) {
            const { parameters, ...toolWithoutParams } = tool;
            return toolWithoutParams;
          }
        } catch (e) {
          // If parsing fails, keep as is (validation should have caught this)
          console.error("Error parsing parameters:", e);
        }
      }

      // Process SIP headers for transfer_call tools
      if (tool.type === "transfer_call" && tool.custom_sip_headers) {
        const sipHeadersObject = tool.custom_sip_headers
          .filter((header) => header.key && header.value)
          .reduce((acc, header) => {
            acc[header.key] = header.value;
            return acc;
          }, {} as Record<string, string>);

        const { custom_sip_headers, ...toolWithoutHeaders } = tool;

        if (Object.keys(sipHeadersObject).length > 0) {
          toolWithoutHeaders.custom_sip_headers = sipHeadersObject;
        } else {
          delete toolWithoutHeaders.custom_sip_headers;
        }
        // Remove temporary state properties for transfer_call
        const {
          transferType,
          displayedPhoneNumber,
          warmTransferOptionType,
          warmTransferMessage,
          warmTransferPrompt,
          ...restOfTool
        } = toolWithoutHeaders;
        return restOfTool;
      }

      // Remove temporary state properties for transfer_call if not handled above
      if (tool.type === "transfer_call") {
        const {
          transferType,
          displayedPhoneNumber,
          warmTransferOptionType,
          warmTransferMessage,
          warmTransferPrompt,
          ...restOfTool
        } = tool;
        return restOfTool;
      }

      return tool;
    });

    // Create a new data object by copying only the properties we want to keep
    const updatedData = {
      ...node.data,
      state_name: stateName,
      state_prompt: statePrompt,
      tools: formattedTools,
    };

    // Only add interruption_sensitivity if it's enabled
    if (enableInterruptionSensitivity) {
      updatedData.interruption_sensitivity = interruptionSensitivityValue;
    } else {
      // Ensure the property is removed if it exists
      delete updatedData.interruption_sensitivity;
    }

    const updatedNode: Node = {
      ...node,
      id: stateName, // Update the node ID to the new state name
      data: updatedData,
    };

    // Pass both the updated node and the original node ID
    onUpdate(updatedNode, originalNodeId);
    onClose();
  };

  const inputProps = {
    bg: isDark ? "gray.700" : "white",
    color: isDark ? "white" : "gray.800",
    borderColor: isDark ? "gray.600" : "gray.300",
    _hover: { borderColor: isDark ? "gray.500" : "gray.400" },
  };

  const labelColor = isDark ? "gray.300" : "gray.700";
  const iconColor = isDark ? "gray.300" : "gray.700";

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent
        bg={isDark ? "gray.800" : "white"}
        color={isDark ? "whiteAlpha.900" : "gray.800"}
        maxW="400px"
      >
        <ModalHeader p={0}>
          <Flex justifyContent="space-between" alignItems="center" p={4} pb={2}>
            <Flex alignItems="center" gap={2}>
              {isEditing ? (
                <Box position="relative">
                  <Input
                    value={stateName}
                    onChange={handleStateNameChange}
                    onBlur={() => {
                      if (validateStateName(stateName)) {
                        setIsEditing(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (validateStateName(stateName)) {
                          setIsEditing(false);
                        }
                      }
                    }}
                    autoFocus
                    size="lg"
                    width="auto"
                    height="auto"
                    fontWeight="medium"
                    _focus={{ borderColor: "transparent" }}
                    borderColor="transparent"
                    variant="filled"
                    outline="none"
                  />
                  {stateNameError && (
                    <Text
                      color="red.500"
                      fontSize="xs"
                      position="absolute"
                      top="100%"
                      left="0"
                    >
                      {stateNameError}
                    </Text>
                  )}
                </Box>
              ) : (
                <>
                  <Text
                    fontSize="lg"
                    fontWeight="medium"
                    onClick={() => setIsEditing(true)}
                    cursor="pointer"
                  >
                    {stateName}
                  </Text>
                  <IconButton
                    aria-label="Edit state name"
                    icon={<EditIcon />}
                    size="xs"
                    variant="ghost"
                    ml={1}
                    onClick={() => setIsEditing(true)}
                    color={isDark ? "gray.400" : "gray.500"}
                    _hover={{
                      color: isDark ? "white" : "gray.800",
                      bg: isDark ? "gray.700" : "gray.200",
                    }}
                  />
                </>
              )}
            </Flex>
            <Flex gap={2}>
              <IconButton
                aria-label="delete"
                icon={<Trash2 size={16} />}
                size="lg"
                variant="ghost"
                onClick={() => {
                  onDelete(node.id);
                  onClose();
                }}
              />
            </Flex>
          </Flex>
          <Divider />
          <Box p={4}>
            {!isStartingNode && (
              <Flex
                alignItems="center"
                gap={2}
                onClick={() => onSetAsStarting(node.id)}
                cursor="pointer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <mask id="path-1-inside-1_3806_25008" fill="white">
                    <path d="M15.8332 10.8337H10.8332V15.8337H9.1665V10.8337H4.1665V9.16699H9.1665V4.16699H10.8332V9.16699H15.8332V10.8337Z" />
                  </mask>
                  <path
                    d="M15.8332 10.8337V12.8337H17.8332V10.8337H15.8332ZM10.8332 10.8337V8.83366H8.83317V10.8337H10.8332ZM10.8332 15.8337V17.8337H12.8332V15.8337H10.8332ZM9.1665 15.8337H7.1665V17.8337H9.1665V15.8337ZM9.1665 10.8337H11.1665V8.83366H9.1665V10.8337ZM4.1665 10.8337H2.1665V12.8337H4.1665V10.8337ZM4.1665 9.16699V7.16699H2.1665V9.16699H4.1665ZM9.1665 9.16699V11.167H11.1665V9.16699H9.1665ZM9.1665 4.16699V2.16699H7.1665V4.16699H9.1665ZM10.8332 4.16699H12.8332V2.16699H10.8332V4.16699ZM10.8332 9.16699H8.83317V11.167H10.8332V9.16699ZM15.8332 9.16699H17.8332V7.16699H15.8332V9.16699ZM15.8332 8.83366H10.8332V12.8337H15.8332V8.83366ZM8.83317 10.8337V15.8337H12.8332V10.8337H8.83317ZM10.8332 13.8337H9.1665V17.8337H10.8332V13.8337ZM11.1665 15.8337V10.8337H7.1665V15.8337H11.1665ZM9.1665 8.83366H4.1665V12.8337H9.1665V8.83366ZM6.1665 10.8337V9.16699H2.1665V10.8337H6.1665ZM4.1665 11.167H9.1665V7.16699H4.1665V11.167ZM11.1665 9.16699V4.16699H7.1665V9.16699H11.1665ZM9.1665 6.16699H10.8332V2.16699H9.1665V6.16699ZM8.83317 4.16699V9.16699H12.8332V4.16699H8.83317ZM10.8332 11.167H15.8332V7.16699H10.8332V11.167ZM13.8332 9.16699V10.8337H17.8332V9.16699H13.8332Z"
                    fill="currentColor"
                    mask="url(#path-1-inside-1_3806_25008)"
                  />
                </svg>
                <Text fontSize="sm" fontWeight="medium">
                  Set as a starting state
                </Text>
              </Flex>
            )}
            {isStartingNode && (
              <Text
                fontSize="sm"
                fontWeight="medium"
                color={isDark ? "gray.300" : "gray.500"}
              >
                This is the starting state
              </Text>
            )}
          </Box>
        </ModalHeader>
        <ModalBody p={4}>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text fontSize="md" fontWeight="medium" mb={2}>
                Prompt
              </Text>
              {/* <Text fontSize="xs" color="gray.500" mb={2}>
                Read{" "}
                <Link
                  href="https://docs.retellai.com/build/write-multi-prompt"
                  isExternal
                  color="gray.500"
                  textDecoration="underline"
                >
                  stateful multi-prompt agent best practices.
                </Link>
              </Text> */}
              <Textarea
                sx={
                  isDark
                    ? {
                        "&::-webkit-scrollbar": {
                          height: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#2D3748", // Dark track color
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#4A5568", // Dark thumb color
                          borderRadius: "2px",
                        },
                        // For Firefox
                        scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                        _hover: {
                          scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                        },
                      }
                    : {
                        "&::-webkit-scrollbar": {
                          height: "4px",
                        },
                        "&::-webkit-scrollbar-button": {
                          display: "none",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#fff",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#fff",
                          borderRadius: "2px",
                        },
                        scrollbarColor: "rgb(220, 220, 220) white",
                        _hover: {
                          scrollbarColor: "rgb(220, 220, 220) white",
                        },
                      }
                }
                value={statePrompt}
                onChange={(e) => setStatePrompt(e.target.value)}
                _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                textColor={isDark ? "white" : "gray.800"}
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.300"}
                _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                _focus={{
                  borderColor: isDark ? "gray.100" : "gray.800",
                }}
                variant="outline"
                resize="none"
                fontSize="sm"
                pr="100px"
                height="100%"
                placeholder={`Enter part of your prompt here, for example, 'List a series of questions to ask the user:
[Question 1]
[Question 2]
[Question 3]
[Question 4]' 
`}
                minHeight="200px"
                size="lg"
              />
            </Box>

            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="md" fontWeight="medium">
                  Tools (Optional)
                </Text>
                <Menu color={isDark ? "white" : "gray.800"}>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDown size={16} />}
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    borderColor={isDark ? "gray.600" : "gray.300"}
                    color={isDark ? "white" : "gray.800"}
                    _hover={{
                      backgroundColor: isDark ? "gray.700" : "gray.200",
                    }}
                  >
                    Add Tool
                  </MenuButton>
                  <MenuList backgroundColor={isDark ? "gray.700" : "white"}>
                    <MenuItem
                      backgroundColor={isDark ? "gray.700" : "white"}
                      _hover={{
                        backgroundColor: isDark ? "gray.600" : "gray.200",
                      }}
                      onClick={() => handleAddTool("end_call")}
                    >
                      End Call
                    </MenuItem>
                    <MenuItem
                      _hover={{
                        backgroundColor: isDark ? "gray.600" : "gray.200",
                      }}
                      backgroundColor={isDark ? "gray.700" : "white"}
                      onClick={() => handleAddTool("transfer_call")}
                    >
                      Call Transfer
                    </MenuItem>
                    <MenuItem
                      _hover={{
                        backgroundColor: isDark ? "gray.600" : "gray.200",
                      }}
                      backgroundColor={isDark ? "gray.700" : "white"}
                      onClick={() => handleAddTool("press_digit")}
                    >
                      Press Digit (IVR Navigation)
                    </MenuItem>
                    <MenuItem
                      _hover={{
                        backgroundColor: isDark ? "gray.600" : "gray.200",
                      }}
                      backgroundColor={isDark ? "gray.700" : "white"}
                      onClick={() => handleAddTool("check_availability_cal")}
                    >
                      Check Availability (Cal.com)
                    </MenuItem>
                    <MenuItem
                      _hover={{
                        backgroundColor: isDark ? "gray.600" : "gray.200",
                      }}
                      backgroundColor={isDark ? "gray.700" : "white"}
                      onClick={() => handleAddTool("book_appointment_cal")}
                    >
                      Book Appointment (Cal.com)
                    </MenuItem>
                    <MenuItem
                      _hover={{
                        backgroundColor: isDark ? "gray.600" : "gray.200",
                      }}
                      backgroundColor={isDark ? "gray.700" : "white"}
                      onClick={() => handleAddTool("custom")}
                    >
                      Custom Function
                    </MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Enable this state with capabilities such as calendar bookings,
                call termination, or your own custom functions.
              </Text>
              <Box>
                {tools.length > 0 ? (
                  <VStack spacing={2} align="stretch">
                    {tools.map((tool, index) => (
                      <Box
                        key={index}
                        p={3}
                        borderWidth="1px"
                        borderRadius="md"
                        mb={3}
                      >
                        <HStack justify="space-between" mb={2}>
                          <Flex alignItems="center">
                            {tool.type === "transfer_call" && (
                              <VscCallOutgoing
                                size={16}
                                style={{ marginRight: "8px" }}
                              />
                            )}
                            <Text fontWeight="bold">
                              {tool.type
                                .split("_")
                                .map(
                                  (word) =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                )
                                .join(" ")}
                            </Text>
                          </Flex>
                          <IconButton
                            aria-label="Delete tool"
                            icon={<Trash2 size={16} />}
                            size="sm"
                            onClick={() => handleDeleteTool(index)}
                          />
                        </HStack>

                        <VStack spacing={3} align="stretch">
                          <FormControl isInvalid={!!toolNameErrors[index]}>
                            <FormLabel fontSize="sm">Name</FormLabel>
                            <Input
                              _placeholder={{
                                color: isDark ? "gray.400" : "gray.600",
                              }}
                              textColor={isDark ? "white" : "gray.800"}
                              bg={isDark ? "gray.700" : "white"}
                              color={isDark ? "white" : "gray.800"}
                              borderColor={isDark ? "gray.600" : "gray.300"}
                              _hover={{
                                borderColor: isDark ? "gray.500" : "gray.400",
                              }}
                              _focus={{
                                borderColor: isDark ? "gray.100" : "gray.800",
                              }}
                              variant="outline"
                              value={tool.name}
                              onChange={(e) =>
                                handleUpdateTool(index, "name", e.target.value)
                              }
                              size="sm"
                              {...inputProps}
                              placeholder="Enter Tool Name"
                            />
                            {toolNameErrors[index] && (
                              <Text color="red.500" fontSize="xs" mt={1}>
                                {toolNameErrors[index]}
                              </Text>
                            )}
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="sm">
                              Description (Optional)
                            </FormLabel>
                            <Textarea
                              sx={
                                isDark
                                  ? {
                                      "&::-webkit-scrollbar": {
                                        height: "8px",
                                      },
                                      "&::-webkit-scrollbar-track": {
                                        backgroundColor: "#2D3748", // Dark track color
                                      },
                                      "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: "#4A5568", // Dark thumb color
                                        borderRadius: "2px",
                                      },
                                      // For Firefox
                                      scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                                      _hover: {
                                        scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                                      },
                                    }
                                  : {
                                      "&::-webkit-scrollbar": {
                                        height: "4px",
                                      },
                                      "&::-webkit-scrollbar-button": {
                                        display: "none",
                                      },
                                      "&::-webkit-scrollbar-track": {
                                        backgroundColor: "#fff",
                                      },
                                      "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: "#fff",
                                        borderRadius: "2px",
                                      },
                                      scrollbarColor:
                                        "rgb(220, 220, 220) white",
                                      _hover: {
                                        scrollbarColor:
                                          "rgb(220, 220, 220) white",
                                      },
                                    }
                              }
                              _placeholder={{
                                color: isDark ? "gray.400" : "gray.600",
                              }}
                              textColor={isDark ? "white" : "gray.800"}
                              bg={isDark ? "gray.700" : "white"}
                              color={isDark ? "white" : "gray.800"}
                              borderColor={isDark ? "gray.600" : "gray.300"}
                              _hover={{
                                borderColor: isDark ? "gray.500" : "gray.400",
                              }}
                              _focus={{
                                borderColor: isDark ? "gray.100" : "gray.800",
                              }}
                              variant="outline"
                              value={tool.description || ""}
                              onChange={(e) =>
                                handleUpdateTool(
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                              size="sm"
                              placeholder="Describe what the tool does"
                              {...inputProps}
                            />
                          </FormControl>

                          {/* Tool-specific fields */}
                          {tool.type === "transfer_call" && (
                            <>
                              <FormControl isRequired>
                                <FormLabel fontSize="sm" color={labelColor}>
                                  Transfer to
                                </FormLabel>
                                <RadioGroup
                                  value={
                                    tool.transfer_destination?.type ||
                                    "predefined"
                                  }
                                  onChange={(value) =>
                                    handleUpdateTool(
                                      index,
                                      "transfer_destination.type",
                                      value
                                    )
                                  }
                                  colorScheme={isDark ? "white" : "black"}
                                >
                                  <HStack spacing={4}>
                                    <Radio
                                      value="predefined"
                                      sx={{
                                        borderWidth: "2px",
                                        borderColor: isDark
                                          ? "gray.500"
                                          : "gray.400",
                                        borderRadius: "full",
                                        width: "15px",
                                        height: "15px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        _checked: {
                                          bg: isDark ? "gray.700" : "gray.200",
                                          borderColor: isDark
                                            ? "white"
                                            : "black",
                                          position: "relative",
                                          _after: {
                                            content: '""',
                                            position: "absolute",
                                            width: "7px",
                                            height: "7px",
                                            bg: "gray.500",
                                            borderRadius: "full",
                                          },
                                        },
                                      }}
                                    >
                                      Static Number
                                    </Radio>
                                    <Radio
                                      value="inferred"
                                      sx={{
                                        borderWidth: "2px",
                                        borderColor: isDark
                                          ? "gray.500"
                                          : "gray.400",
                                        borderRadius: "full",
                                        width: "15px",
                                        height: "15px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        _checked: {
                                          bg: isDark ? "gray.700" : "gray.200",
                                          borderColor: isDark
                                            ? "white"
                                            : "black",
                                          position: "relative",
                                          _after: {
                                            content: '""',
                                            position: "absolute",
                                            width: "7px",
                                            height: "7px",
                                            bg: "gray.500",
                                            borderRadius: "full",
                                          },
                                        },
                                      }}
                                    >
                                      Dynamic Routing
                                    </Radio>
                                  </HStack>
                                </RadioGroup>

                                {tool.transfer_destination?.type ===
                                  "predefined" ||
                                !tool.transfer_destination?.type ? (
                                  <Input
                                    _placeholder={{
                                      color: isDark ? "gray.400" : "gray.600",
                                    }}
                                    textColor={isDark ? "white" : "gray.800"}
                                    bg={isDark ? "gray.700" : "white"}
                                    color={isDark ? "white" : "gray.800"}
                                    borderColor={
                                      isDark ? "gray.600" : "gray.300"
                                    }
                                    _hover={{
                                      borderColor: isDark
                                        ? "gray.500"
                                        : "gray.400",
                                    }}
                                    _focus={{
                                      borderColor: isDark
                                        ? "gray.100"
                                        : "gray.800",
                                    }}
                                    variant="outline"
                                    mt={2}
                                    value={
                                      tool.transfer_destination?.number || ""
                                    }
                                    onChange={(e) =>
                                      handleUpdateTool(
                                        index,
                                        "transfer_destination.number",
                                        e.target.value
                                      )
                                    }
                                    placeholder="+12134447777"
                                    size="sm"
                                    {...inputProps}
                                  />
                                ) : (
                                  <Textarea
                                    sx={
                                      isDark
                                        ? {
                                            "&::-webkit-scrollbar": {
                                              height: "8px",
                                            },
                                            "&::-webkit-scrollbar-track": {
                                              backgroundColor: "#2D3748", // Dark track color
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                              backgroundColor: "#4A5568", // Dark thumb color
                                              borderRadius: "2px",
                                            },
                                            // For Firefox
                                            scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                                            _hover: {
                                              scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                                            },
                                          }
                                        : {
                                            "&::-webkit-scrollbar": {
                                              height: "4px",
                                            },
                                            "&::-webkit-scrollbar-button": {
                                              display: "none",
                                            },
                                            "&::-webkit-scrollbar-track": {
                                              backgroundColor: "#fff",
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                              backgroundColor: "#fff",
                                              borderRadius: "2px",
                                            },
                                            scrollbarColor:
                                              "rgb(220, 220, 220) white",
                                            _hover: {
                                              scrollbarColor:
                                                "rgb(220, 220, 220) white",
                                            },
                                          }
                                    }
                                    _placeholder={{
                                      color: isDark ? "gray.400" : "gray.600",
                                    }}
                                    textColor={isDark ? "white" : "gray.800"}
                                    bg={isDark ? "gray.700" : "white"}
                                    color={isDark ? "white" : "gray.800"}
                                    borderColor={
                                      isDark ? "gray.600" : "gray.300"
                                    }
                                    _hover={{
                                      borderColor: isDark
                                        ? "gray.500"
                                        : "gray.400",
                                    }}
                                    _focus={{
                                      borderColor: isDark
                                        ? "gray.100"
                                        : "gray.800",
                                    }}
                                    variant="outline"
                                    mt={2}
                                    value={
                                      tool.transfer_destination?.prompt || ""
                                    }
                                    onChange={(e) =>
                                      handleUpdateTool(
                                        index,
                                        "transfer_destination.prompt",
                                        e.target.value
                                      )
                                    }
                                    placeholder="Enter prompt to infer the destination number"
                                    size="sm"
                                    rows={3}
                                    {...inputProps}
                                  />
                                )}
                              </FormControl>

                              <FormControl isRequired>
                                <FormLabel fontSize="sm" color={labelColor}>
                                  Type
                                </FormLabel>
                                <RadioGroup
                                  value={
                                    tool.transfer_option?.type ===
                                    "warm_transfer"
                                      ? "warm"
                                      : "cold"
                                  }
                                  onChange={(value) => {
                                    handleUpdateTool(
                                      index,
                                      "transfer_option.type",
                                      value === "warm"
                                        ? "warm_transfer"
                                        : "cold_transfer"
                                    );
                                  }}
                                  colorScheme={isDark ? "white" : "black"}
                                >
                                  <HStack spacing={4}>
                                    <Radio
                                      value="cold"
                                      sx={{
                                        borderWidth: "2px",
                                        borderColor: isDark
                                          ? "gray.500"
                                          : "gray.400",
                                        borderRadius: "full",
                                        width: "15px",
                                        height: "15px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        _checked: {
                                          bg: isDark ? "gray.700" : "gray.200",
                                          borderColor: isDark
                                            ? "white"
                                            : "black",
                                          position: "relative",
                                          _after: {
                                            content: '""',
                                            position: "absolute",
                                            width: "7px",
                                            height: "7px",
                                            bg: "gray.500",
                                            borderRadius: "full",
                                          },
                                        },
                                      }}
                                    >
                                      Cold Transfer
                                      <Tooltip label="AI transfers the call to the next agent without a debrief.">
                                        <IconButton
                                          aria-label="Cold transfer info"
                                          icon={<Info size={14} />}
                                          size="xs"
                                          variant="ghost"
                                          ml={1}
                                          color={iconColor}
                                        />
                                      </Tooltip>
                                    </Radio>
                                    <Radio
                                      value="warm"
                                      sx={{
                                        borderWidth: "2px",
                                        borderColor: isDark
                                          ? "gray.500"
                                          : "gray.400",
                                        borderRadius: "full",
                                        width: "15px",
                                        height: "15px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        _checked: {
                                          bg: isDark ? "gray.700" : "gray.200",
                                          borderColor: isDark
                                            ? "white"
                                            : "black",
                                          position: "relative",
                                          _after: {
                                            content: '""',
                                            position: "absolute",
                                            width: "7px",
                                            height: "7px",
                                            bg: "gray.500",
                                            borderRadius: "full",
                                          },
                                        },
                                      }}
                                    >
                                      Warm Transfer
                                      <Tooltip label="AI provides a debrief to the next agent after transferring the call.">
                                        <IconButton
                                          aria-label="Warm transfer info"
                                          icon={<Info size={14} />}
                                          size="xs"
                                          variant="ghost"
                                          ml={1}
                                          color={iconColor}
                                        />
                                      </Tooltip>
                                    </Radio>
                                  </HStack>
                                </RadioGroup>
                              </FormControl>

                              {tool.transfer_option?.type ===
                                "cold_transfer" && (
                                <FormControl>
                                  <FormLabel fontSize="sm" color={labelColor}>
                                    Displayed Phone Number
                                  </FormLabel>
                                  <RadioGroup
                                    value={
                                      tool.transfer_option
                                        ?.show_transferee_as_caller
                                        ? "transferee"
                                        : "retell"
                                    }
                                    onChange={(value) =>
                                      handleUpdateTool(
                                        index,
                                        "transfer_option.show_transferee_as_caller",
                                        value === "transferee"
                                      )
                                    }
                                    colorScheme={isDark ? "white" : "black"}
                                  >
                                    <HStack spacing={4}>
                                      <Radio
                                        value="retell"
                                        sx={{
                                          borderWidth: "2px",
                                          borderColor: isDark
                                            ? "gray.500"
                                            : "gray.400",
                                          borderRadius: "full",
                                          width: "15px",
                                          height: "15px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          _checked: {
                                            bg: isDark
                                              ? "gray.700"
                                              : "gray.200",
                                            borderColor: isDark
                                              ? "white"
                                              : "black",
                                            position: "relative",
                                            _after: {
                                              content: '""',
                                              position: "absolute",
                                              width: "7px",
                                              height: "7px",
                                              bg: "gray.500",
                                              borderRadius: "full",
                                            },
                                          },
                                        }}
                                      >
                                        Agent's Number
                                      </Radio>
                                      <Radio
                                        value="transferee"
                                        sx={{
                                          borderWidth: "2px",
                                          borderColor: isDark
                                            ? "gray.500"
                                            : "gray.400",
                                          borderRadius: "full",
                                          width: "15px",
                                          height: "15px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          _checked: {
                                            bg: isDark
                                              ? "gray.700"
                                              : "gray.200",
                                            borderColor: isDark
                                              ? "white"
                                              : "black",
                                            position: "relative",
                                            _after: {
                                              content: '""',
                                              position: "absolute",
                                              width: "7px",
                                              height: "7px",
                                              bg: "gray.500",
                                              borderRadius: "full",
                                            },
                                          },
                                        }}
                                      >
                                        Transferee's Number
                                        <Tooltip label="Original Caller or Callee. If you are using custom telephony, enable SIP REFER and PSTN transfer, and set SIP REFER to show transferee's number.">
                                          <IconButton
                                            aria-label="Transferee's number info"
                                            icon={<Info size={14} />}
                                            size="xs"
                                            variant="ghost"
                                            ml={1}
                                            color={iconColor}
                                          />
                                        </Tooltip>
                                      </Radio>
                                    </HStack>
                                  </RadioGroup>
                                </FormControl>
                              )}

                              {tool.transfer_option?.type ===
                                "warm_transfer" && (
                                <FormControl isRequired>
                                  <FormLabel fontSize="sm" color={labelColor}>
                                    Handoff Message
                                  </FormLabel>
                                  <RadioGroup
                                    value={
                                      tool.transfer_option
                                        ?.public_handoff_option?.type ||
                                      "static_message"
                                    }
                                    onChange={(value) =>
                                      handleUpdateTool(
                                        index,
                                        "transfer_option.public_handoff_option.type",
                                        value
                                      )
                                    }
                                    colorScheme={isDark ? "white" : "black"}
                                  >
                                    <HStack spacing={4}>
                                      <Radio
                                        value="static_message"
                                        sx={{
                                          borderWidth: "2px",
                                          borderColor: isDark
                                            ? "gray.500"
                                            : "gray.400",
                                          borderRadius: "full",
                                          width: "15px",
                                          height: "15px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          _checked: {
                                            bg: isDark
                                              ? "gray.700"
                                              : "gray.200",
                                            borderColor: isDark
                                              ? "white"
                                              : "black",
                                            position: "relative",
                                            _after: {
                                              content: '""',
                                              position: "absolute",
                                              width: "7px",
                                              height: "7px",
                                              bg: "gray.500",
                                              borderRadius: "full",
                                            },
                                          },
                                        }}
                                      >
                                        Static Sentence
                                      </Radio>
                                      <Radio
                                        value="prompt"
                                        sx={{
                                          borderWidth: "2px",
                                          borderColor: isDark
                                            ? "gray.500"
                                            : "gray.400",
                                          borderRadius: "full",
                                          width: "15px",
                                          height: "15px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          _checked: {
                                            bg: isDark
                                              ? "gray.700"
                                              : "gray.200",
                                            borderColor: isDark
                                              ? "white"
                                              : "black",
                                            position: "relative",
                                            _after: {
                                              content: '""',
                                              position: "absolute",
                                              width: "7px",
                                              height: "7px",
                                              bg: "gray.500",
                                              borderRadius: "full",
                                            },
                                          },
                                        }}
                                      >
                                        Prompt
                                      </Radio>
                                    </HStack>
                                  </RadioGroup>

                                  {tool.transfer_option?.public_handoff_option
                                    ?.type === "static_message" ||
                                  !tool.transfer_option?.public_handoff_option
                                    ?.type ? (
                                    <Textarea
                                      sx={
                                        isDark
                                          ? {
                                              "&::-webkit-scrollbar": {
                                                height: "8px",
                                              },
                                              "&::-webkit-scrollbar-track": {
                                                backgroundColor: "#2D3748", // Dark track color
                                              },
                                              "&::-webkit-scrollbar-thumb": {
                                                backgroundColor: "#4A5568", // Dark thumb color
                                                borderRadius: "2px",
                                              },
                                              // For Firefox
                                              scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                                              _hover: {
                                                scrollbarColor:
                                                  "#4A5568 #2D3748", // same colors on hover
                                              },
                                            }
                                          : {
                                              "&::-webkit-scrollbar": {
                                                height: "4px",
                                              },
                                              "&::-webkit-scrollbar-button": {
                                                display: "none",
                                              },
                                              "&::-webkit-scrollbar-track": {
                                                backgroundColor: "#fff",
                                              },
                                              "&::-webkit-scrollbar-thumb": {
                                                backgroundColor: "#fff",
                                                borderRadius: "2px",
                                              },
                                              scrollbarColor:
                                                "rgb(220, 220, 220) white",
                                              _hover: {
                                                scrollbarColor:
                                                  "rgb(220, 220, 220) white",
                                              },
                                            }
                                      }
                                      _placeholder={{
                                        color: isDark ? "gray.400" : "gray.600",
                                      }}
                                      textColor={isDark ? "white" : "gray.800"}
                                      bg={isDark ? "gray.700" : "white"}
                                      color={isDark ? "white" : "gray.800"}
                                      borderColor={
                                        isDark ? "gray.600" : "gray.300"
                                      }
                                      _hover={{
                                        borderColor: isDark
                                          ? "gray.500"
                                          : "gray.400",
                                      }}
                                      _focus={{
                                        borderColor: isDark
                                          ? "gray.100"
                                          : "gray.800",
                                      }}
                                      variant="outline"
                                      mt={2}
                                      value={
                                        tool.transfer_option
                                          ?.public_handoff_option?.message || ""
                                      }
                                      onChange={(e) =>
                                        handleUpdateTool(
                                          index,
                                          "transfer_option.public_handoff_option.message",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter static message"
                                      size="sm"
                                      rows={3}
                                      {...inputProps}
                                    />
                                  ) : (
                                    <Textarea
                                      sx={
                                        isDark
                                          ? {
                                              "&::-webkit-scrollbar": {
                                                height: "8px",
                                              },
                                              "&::-webkit-scrollbar-track": {
                                                backgroundColor: "#2D3748", // Dark track color
                                              },
                                              "&::-webkit-scrollbar-thumb": {
                                                backgroundColor: "#4A5568", // Dark thumb color
                                                borderRadius: "2px",
                                              },
                                              // For Firefox
                                              scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                                              _hover: {
                                                scrollbarColor:
                                                  "#4A5568 #2D3748", // same colors on hover
                                              },
                                            }
                                          : {
                                              "&::-webkit-scrollbar": {
                                                height: "4px",
                                              },
                                              "&::-webkit-scrollbar-button": {
                                                display: "none",
                                              },
                                              "&::-webkit-scrollbar-track": {
                                                backgroundColor: "#fff",
                                              },
                                              "&::-webkit-scrollbar-thumb": {
                                                backgroundColor: "#fff",
                                                borderRadius: "2px",
                                              },
                                              scrollbarColor:
                                                "rgb(220, 220, 220) white",
                                              _hover: {
                                                scrollbarColor:
                                                  "rgb(220, 220, 220) white",
                                              },
                                            }
                                      }
                                      _placeholder={{
                                        color: isDark ? "gray.400" : "gray.600",
                                      }}
                                      textColor={isDark ? "white" : "gray.800"}
                                      bg={isDark ? "gray.700" : "white"}
                                      color={isDark ? "white" : "gray.800"}
                                      borderColor={
                                        isDark ? "gray.600" : "gray.300"
                                      }
                                      _hover={{
                                        borderColor: isDark
                                          ? "gray.500"
                                          : "gray.400",
                                      }}
                                      _focus={{
                                        borderColor: isDark
                                          ? "gray.100"
                                          : "gray.800",
                                      }}
                                      variant="outline"
                                      mt={2}
                                      value={
                                        tool.transfer_option
                                          ?.public_handoff_option?.prompt || ""
                                      }
                                      onChange={(e) =>
                                        handleUpdateTool(
                                          index,
                                          "transfer_option.public_handoff_option.prompt",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Say hello to the agent and summarize the user problem to him"
                                      size="sm"
                                      rows={3}
                                      {...inputProps}
                                    />
                                  )}
                                </FormControl>
                              )}

                              <FormControl>
                                <FormLabel fontSize="sm">
                                  Custom SIP Headers
                                </FormLabel>
                                <Text fontSize="xs" color="gray.500" mb={2}>
                                  Add key/value pairs for call routing,
                                  metadata, or carrier integration.
                                </Text>

                                {tool.custom_sip_headers &&
                                tool.custom_sip_headers.length > 0
                                  ? tool.custom_sip_headers.map(
                                      (header, headerIndex) => (
                                        <VStack
                                          key={headerIndex}
                                          spacing={1}
                                          mb={3}
                                          align="stretch"
                                        >
                                          <HStack spacing={2}>
                                            <FormControl
                                              isInvalid={!!header.error}
                                              flex={1}
                                            >
                                              <Input
                                                placeholder="Key"
                                                value={header.key}
                                                onChange={(e) =>
                                                  handleUpdateSipHeader(
                                                    index,
                                                    headerIndex,
                                                    "key",
                                                    e.target.value
                                                  )
                                                }
                                                size="sm"
                                                {...inputProps}
                                              />
                                            </FormControl>
                                            <Input
                                              placeholder="Value"
                                              value={header.value}
                                              onChange={(e) =>
                                                handleUpdateSipHeader(
                                                  index,
                                                  headerIndex,
                                                  "value",
                                                  e.target.value
                                                )
                                              }
                                              size="sm"
                                              flex={1}
                                              {...inputProps}
                                            />
                                            <IconButton
                                              aria-label="Remove header"
                                              icon={<Trash2 size={16} />}
                                              size="sm"
                                              variant="ghost"
                                              onClick={() =>
                                                handleRemoveSipHeader(
                                                  index,
                                                  headerIndex
                                                )
                                              }
                                              flexShrink={0}
                                            />
                                          </HStack>
                                          {header.error && (
                                            <Text
                                              color="red.500"
                                              fontSize="xs"
                                              ml={0}
                                            >
                                              {header.error}
                                            </Text>
                                          )}
                                        </VStack>
                                      )
                                    )
                                  : null}

                                <Button
                                  leftIcon={
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M5 12h14" />
                                      <path d="M12 5v14" />
                                    </svg>
                                  }
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAddSipHeader(index)}
                                  color={isDark ? "gray.300" : "gray.600"}
                                  _hover={{
                                    bg: isDark ? "gray.700" : "gray.100",
                                  }}
                                >
                                  Add
                                </Button>
                              </FormControl>
                            </>
                          )}

                          {tool.type === "end_call" && (
                            <Text fontSize="sm" color="gray.500">
                              This tool will end the current call.
                            </Text>
                          )}

                          {tool.type === "press_digit" && (
                            <FormControl>
                              <FormLabel fontSize="sm">Delay (ms)</FormLabel>
                              <Input
                                _placeholder={{
                                  color: isDark ? "gray.400" : "gray.600",
                                }}
                                textColor={isDark ? "white" : "gray.800"}
                                bg={isDark ? "gray.700" : "white"}
                                color={isDark ? "white" : "gray.800"}
                                borderColor={isDark ? "gray.600" : "gray.300"}
                                _hover={{
                                  borderColor: isDark ? "gray.500" : "gray.400",
                                }}
                                _focus={{
                                  borderColor: isDark ? "gray.100" : "gray.800",
                                }}
                                variant="outline"
                                type="number"
                                value={tool.delay_ms || 1000}
                                onChange={(e) => {
                                  // Ensure value is within valid range (0-5000ms)
                                  const value = Number.parseInt(e.target.value);
                                  const validValue = Math.min(
                                    Math.max(0, value),
                                    5000
                                  );
                                  handleUpdateTool(
                                    index,
                                    "delay_ms",
                                    validValue
                                  );
                                }}
                                placeholder="Delay in milliseconds (0-5000)"
                                size="sm"
                                min={0}
                                max={5000}
                                {...inputProps}
                              />
                              <Text fontSize="xs" color="gray.500" mt={1}>
                                Delay before pressing the digit (0-5000ms).
                                Default: 1000ms (1s)
                              </Text>
                            </FormControl>
                          )}

                          {(tool.type === "check_availability_cal" ||
                            tool.type === "book_appointment_cal") && (
                            <>
                              <FormControl>
                                <FormLabel fontSize="sm">
                                  API Key (Cal.com)
                                </FormLabel>
                                <Input
                                  _placeholder={{
                                    color: isDark ? "gray.400" : "gray.600",
                                  }}
                                  textColor={isDark ? "white" : "gray.800"}
                                  bg={isDark ? "gray.700" : "white"}
                                  color={isDark ? "white" : "gray.800"}
                                  borderColor={isDark ? "gray.600" : "gray.300"}
                                  _hover={{
                                    borderColor: isDark
                                      ? "gray.500"
                                      : "gray.400",
                                  }}
                                  _focus={{
                                    borderColor: isDark
                                      ? "gray.100"
                                      : "gray.800",
                                  }}
                                  variant="outline"
                                  value={tool.cal_api_key || ""}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "cal_api_key",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Enter your Cal.com API key"
                                  size="sm"
                                  {...inputProps}
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="sm">
                                  Event Type ID (Cal.com)
                                </FormLabel>
                                <Input
                                  _placeholder={{
                                    color: isDark ? "gray.400" : "gray.600",
                                  }}
                                  textColor={isDark ? "white" : "gray.800"}
                                  bg={isDark ? "gray.700" : "white"}
                                  color={isDark ? "white" : "gray.800"}
                                  borderColor={isDark ? "gray.600" : "gray.300"}
                                  _hover={{
                                    borderColor: isDark
                                      ? "gray.500"
                                      : "gray.400",
                                  }}
                                  _focus={{
                                    borderColor: isDark
                                      ? "gray.100"
                                      : "gray.800",
                                  }}
                                  variant="outline"
                                  type="number"
                                  value={tool.event_type_id || ""}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "event_type_id",
                                      Number(e.target.value)
                                    )
                                  }
                                  placeholder="Enter event type ID"
                                  size="sm"
                                  {...inputProps}
                                />
                              </FormControl>
                              <FormControl>
                                <FormLabel fontSize="sm">
                                  Timezone (Optional)
                                </FormLabel>
                                <Input
                                  _placeholder={{
                                    color: isDark ? "gray.400" : "gray.600",
                                  }}
                                  textColor={isDark ? "white" : "gray.800"}
                                  bg={isDark ? "gray.700" : "white"}
                                  color={isDark ? "white" : "gray.800"}
                                  borderColor={isDark ? "gray.600" : "gray.300"}
                                  _hover={{
                                    borderColor: isDark
                                      ? "gray.500"
                                      : "gray.400",
                                  }}
                                  _focus={{
                                    borderColor: isDark
                                      ? "gray.100"
                                      : "gray.800",
                                  }}
                                  variant="outline"
                                  value={tool.timezone || "America/Los_Angeles"}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "timezone",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Enter timezone"
                                  size="sm"
                                  {...inputProps}
                                />
                              </FormControl>
                            </>
                          )}

                          {tool.type === "custom" && (
                            <>
                              <FormControl isRequired>
                                <FormLabel fontSize="sm">Your URL</FormLabel>
                                <Input
                                  _placeholder={{
                                    color: isDark ? "gray.400" : "gray.600",
                                  }}
                                  textColor={isDark ? "white" : "gray.800"}
                                  bg={isDark ? "gray.700" : "white"}
                                  color={isDark ? "white" : "gray.800"}
                                  borderColor={isDark ? "gray.600" : "gray.300"}
                                  _hover={{
                                    borderColor: isDark
                                      ? "gray.500"
                                      : "gray.400",
                                  }}
                                  _focus={{
                                    borderColor: isDark
                                      ? "gray.100"
                                      : "gray.800",
                                  }}
                                  variant="outline"
                                  value={tool.url || ""}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "url",
                                      e.target.value
                                    )
                                  }
                                  placeholder="https://your-api-endpoint.com/function"
                                  size="sm"
                                  {...inputProps}
                                />
                              </FormControl>

                              <FormControl>
                                <FormLabel fontSize="sm">
                                  Parameters (Optional)
                                </FormLabel>
                                <Text fontSize="xs" color="gray.500" mb={2}>
                                  JSON schema that defines the format in which
                                  the LLM will return. Please refer to the docs.
                                </Text>
                                <Textarea
                                  sx={
                                    isDark
                                      ? {
                                          "&::-webkit-scrollbar": {
                                            height: "8px",
                                          },
                                          "&::-webkit-scrollbar-track": {
                                            backgroundColor: "#2D3748", // Dark track color
                                          },
                                          "&::-webkit-scrollbar-thumb": {
                                            backgroundColor: "#4A5568", // Dark thumb color
                                            borderRadius: "2px",
                                          },
                                          // For Firefox
                                          scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                                          _hover: {
                                            scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                                          },
                                        }
                                      : {
                                          "&::-webkit-scrollbar": {
                                            height: "4px",
                                          },
                                          "&::-webkit-scrollbar-button": {
                                            display: "none",
                                          },
                                          "&::-webkit-scrollbar-track": {
                                            backgroundColor: "#fff",
                                          },
                                          "&::-webkit-scrollbar-thumb": {
                                            backgroundColor: "#fff",
                                            borderRadius: "2px",
                                          },
                                          scrollbarColor:
                                            "rgb(220, 220, 220) white",
                                          _hover: {
                                            scrollbarColor:
                                              "rgb(220, 220, 220) white",
                                          },
                                        }
                                  }
                                  _placeholder={{
                                    color: isDark ? "gray.400" : "gray.600",
                                  }}
                                  textColor={isDark ? "white" : "gray.800"}
                                  bg={isDark ? "gray.700" : "white"}
                                  color={isDark ? "white" : "gray.800"}
                                  borderColor={isDark ? "gray.600" : "gray.300"}
                                  _hover={{
                                    borderColor: isDark
                                      ? "gray.500"
                                      : "gray.400",
                                  }}
                                  _focus={{
                                    borderColor: isDark
                                      ? "gray.100"
                                      : "gray.800",
                                  }}
                                  variant="outline"
                                  value={tool.parameters || ""}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "parameters",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Enter JSON Schema here..."
                                  size="sm"
                                  rows={6}
                                  fontFamily="monospace"
                                  {...inputProps}
                                />
                                {jsonSchemaError[index] && (
                                  <Text color="red.500" fontSize="xs" mt={1}>
                                    {jsonSchemaError[index]}
                                  </Text>
                                )}
                                <HStack mt={2} spacing={2}>
                                  <Button
                                    colorScheme="gray"
                                    _hover={{
                                      bg: isDark ? "gray.600" : "gray.300",
                                    }}
                                    background={
                                      isDark ? "gray.700" : "gray.200"
                                    }
                                    textColor={
                                      isDark ? "whiteAlpha" : "gray.800"
                                    }
                                    size="xs"
                                    onClick={() =>
                                      handleUpdateTool(
                                        index,
                                        "parameters",
                                        JSON.stringify(
                                          exampleSchemas[0],
                                          null,
                                          2
                                        )
                                      )
                                    }
                                  >
                                    example 1
                                  </Button>
                                  <Button
                                    colorScheme="gray"
                                    _hover={{
                                      bg: isDark ? "gray.600" : "gray.300",
                                    }}
                                    background={
                                      isDark ? "gray.700" : "gray.200"
                                    }
                                    textColor={
                                      isDark ? "whiteAlpha" : "gray.800"
                                    }
                                    size="xs"
                                    onClick={() =>
                                      handleUpdateTool(
                                        index,
                                        "parameters",
                                        JSON.stringify(
                                          exampleSchemas[1],
                                          null,
                                          2
                                        )
                                      )
                                    }
                                  >
                                    example 2
                                  </Button>
                                  <Button
                                    colorScheme="gray"
                                    _hover={{
                                      bg: isDark ? "gray.600" : "gray.300",
                                    }}
                                    background={
                                      isDark ? "gray.700" : "gray.200"
                                    }
                                    textColor={
                                      isDark ? "whiteAlpha" : "gray.800"
                                    }
                                    size="xs"
                                    onClick={() =>
                                      handleUpdateTool(
                                        index,
                                        "parameters",
                                        JSON.stringify(
                                          exampleSchemas[2],
                                          null,
                                          2
                                        )
                                      )
                                    }
                                  >
                                    example 3
                                  </Button>
                                </HStack>
                                <Button
                                  colorScheme="gray"
                                  _hover={{
                                    bg: isDark ? "gray.600" : "gray.300",
                                  }}
                                  background={isDark ? "gray.700" : "gray.200"}
                                  textColor={isDark ? "whiteAlpha" : "gray.800"}
                                  mt={2}
                                  size="sm"
                                  width="100%"
                                  onClick={() => formatJsonSchema(index)}
                                >
                                  Format JSON
                                </Button>
                              </FormControl>

                              <FormControl>
                                <Checkbox
                                  isChecked={tool.speak_during_execution}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "speak_during_execution",
                                      e.target.checked
                                    )
                                  }
                                  colorScheme="gray"
                                  size="sm"
                                  mt={2}
                                >
                                  <Text fontSize="sm" fontWeight="medium">
                                    Speak During Execution
                                  </Text>
                                </Checkbox>
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  ml={6}
                                  mt={1}
                                >
                                  If the function takes over 2 seconds, the
                                  agent can say something like: "Let me check
                                  that for you."
                                </Text>

                                {tool.speak_during_execution && (
                                  <Textarea
                                    sx={
                                      isDark
                                        ? {
                                            "&::-webkit-scrollbar": {
                                              height: "8px",
                                            },
                                            "&::-webkit-scrollbar-track": {
                                              backgroundColor: "#2D3748", // Dark track color
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                              backgroundColor: "#4A5568", // Dark thumb color
                                              borderRadius: "2px",
                                            },
                                            // For Firefox
                                            scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                                            _hover: {
                                              scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                                            },
                                          }
                                        : {
                                            "&::-webkit-scrollbar": {
                                              height: "4px",
                                            },
                                            "&::-webkit-scrollbar-button": {
                                              display: "none",
                                            },
                                            "&::-webkit-scrollbar-track": {
                                              backgroundColor: "#fff",
                                            },
                                            "&::-webkit-scrollbar-thumb": {
                                              backgroundColor: "#fff",
                                              borderRadius: "2px",
                                            },
                                            scrollbarColor:
                                              "rgb(220, 220, 220) white",
                                            _hover: {
                                              scrollbarColor:
                                                "rgb(220, 220, 220) white",
                                            },
                                          }
                                    }
                                    _placeholder={{
                                      color: isDark ? "gray.400" : "gray.600",
                                    }}
                                    textColor={isDark ? "white" : "gray.800"}
                                    bg={isDark ? "gray.700" : "white"}
                                    color={isDark ? "white" : "gray.800"}
                                    borderColor={
                                      isDark ? "gray.600" : "gray.300"
                                    }
                                    _hover={{
                                      borderColor: isDark
                                        ? "gray.500"
                                        : "gray.400",
                                    }}
                                    _focus={{
                                      borderColor: isDark
                                        ? "gray.100"
                                        : "gray.800",
                                    }}
                                    variant="outline"
                                    mt={2}
                                    value={
                                      tool.execution_message_description || ""
                                    }
                                    onChange={(e) =>
                                      handleUpdateTool(
                                        index,
                                        "execution_message_description",
                                        e.target.value
                                      )
                                    }
                                    placeholder="The message you will say to user when calling this tool. Make sure it fits into the conversation smoothly."
                                    size="sm"
                                    rows={3}
                                    {...inputProps}
                                  />
                                )}
                              </FormControl>

                              <FormControl>
                                <Checkbox
                                  isChecked={tool.speak_after_execution}
                                  onChange={(e) =>
                                    handleUpdateTool(
                                      index,
                                      "speak_after_execution",
                                      e.target.checked
                                    )
                                  }
                                  colorScheme="gray"
                                  size="sm"
                                  mt={2}
                                >
                                  <Text fontSize="sm" fontWeight="medium">
                                    Speak After Execution
                                  </Text>
                                </Checkbox>
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  ml={6}
                                  mt={1}
                                >
                                  Unselect if you want to run the function
                                  silently, such as uploading the call result to
                                  the server silently.
                                </Text>
                              </FormControl>

                              <FormControl>
                                <FormLabel fontSize="sm">
                                  Timeout (ms)
                                </FormLabel>
                                <Input
                                  _placeholder={{
                                    color: isDark ? "gray.400" : "gray.600",
                                  }}
                                  textColor={isDark ? "white" : "gray.800"}
                                  bg={isDark ? "gray.700" : "white"}
                                  color={isDark ? "white" : "gray.800"}
                                  borderColor={isDark ? "gray.600" : "gray.300"}
                                  _hover={{
                                    borderColor: isDark
                                      ? "gray.500"
                                      : "gray.400",
                                  }}
                                  _focus={{
                                    borderColor: isDark
                                      ? "gray.100"
                                      : "gray.800",
                                  }}
                                  variant="outline"
                                  type="number"
                                  value={tool.timeout_ms || 120000}
                                  onChange={(e) => {
                                    // Ensure value is within valid range (1000-600000ms)
                                    const value = Number.parseInt(
                                      e.target.value
                                    );
                                    const validValue = Math.min(
                                      Math.max(1000, value),
                                      600000
                                    );
                                    handleUpdateTool(
                                      index,
                                      "timeout_ms",
                                      validValue
                                    );
                                  }}
                                  placeholder="Timeout in milliseconds (1000-600000)"
                                  size="sm"
                                  min={1000}
                                  max={600000}
                                  {...inputProps}
                                />
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                  Maximum execution time (1000-600000ms).
                                  Default: 120000ms (2min)
                                </Text>
                              </FormControl>
                            </>
                          )}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    textAlign="center"
                    py={4}
                  >
                    No tools added yet. Click "Add Tool" to add a tool.
                  </Text>
                )}
              </Box>
            </Box>

            <Divider mt={8} />

            <Box>
              <Checkbox
                isChecked={enableInterruptionSensitivity}
                onChange={(e) =>
                  setEnableInterruptionSensitivity(e.target.checked)
                }
                colorScheme="gray"
                size="sm"
                mt={2}
              >
                <Text fontSize="sm" fontWeight="medium">
                  Enable State Interruption Sensitivity
                </Text>
              </Checkbox>

              {enableInterruptionSensitivity && (
                <Box mt={2}>
                  <Text fontSize="sm" color="gray.500" mb={1}>
                    Interruption Sensitivity (
                    {interruptionSensitivityValue.toFixed(2)})
                  </Text>
                  <Text fontSize="xs" color="gray.500" mb={2}>
                    Override the default interruption sensitivity for this state
                  </Text>
                  <HStack spacing={4}>
                    <Text fontSize="xs" color="gray.500">
                      Low
                    </Text>
                    <Slider
                      aria-label="Interruption Sensitivity"
                      min={0}
                      max={1}
                      step={0.01}
                      value={interruptionSensitivityValue}
                      onChange={(value) =>
                        setInterruptionSensitivityValue(value)
                      }
                      flex={1}
                    >
                      <SliderTrack bg={isDark ? "gray.600" : "gray.200"}>
                        <SliderFilledTrack bg="gray.500" />
                      </SliderTrack>
                      <SliderThumb boxSize={4} />
                    </Slider>
                    <Text fontSize="xs" color="gray.500">
                      High
                    </Text>
                  </HStack>
                </Box>
              )}
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme={isDark ? "black" : "white"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
            variant="ghost"
            mr={3}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="gray"
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
            onClick={handleSave}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
